import { useSearchParams } from 'react-router-dom';
import { useServices, useToast } from '@/app/providers';
import { AsyncSearchableSelect, type SelectOption, DataTable, type ColumnDef } from '@/core/ui/components';
import { useCrudNotifications, usePaginatedList, useDateFormatter, useFileUpload } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import {
  Charge,
  ChargeStatus,
  CreateManualPaymentDTO,
  Payment,
  PaymentMethod,
  PaymentStatus,
  UpdateManualPaymentDTO,
  VerifyPaymentDTO,
} from '@/shared/types';
import { formatUnitLabel, type QueryParams } from '@yawi/domain';
import { AlertTriangle, CheckCircle, Clock, DollarSign, Eye, FileText, Filter, Image, Loader2, Pencil, Plus, Upload, User, X, XCircle } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../atoms/Button';
import { useAbility } from '@/modules/identity/application/ability';
import { Input } from '../atoms/Input';

const toUtcDateString = (d: Date = new Date()) =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

export const AdminPaymentVerificationList = () => {
  const { t } = useI18n();
  const ability = useAbility();
  const { formatDate } = useDateFormatter();
  const toast = useToast();
  const { paymentRepo, chargeRepo, unitRepo } = useServices();
  const { withNotification, withCreateNotification } = useCrudNotifications({
    entityName: 'Pago',
  });
  const canManagePayment = ability.can('manage', 'all') || ability.can('manage', 'Payment');
  const canUpdatePayment = canManagePayment || ability.can('update', 'Payment');

  // Permite entrar filtrado desde un enlace profundo, p.ej. la alerta "Pagos por
  // verificar" del dashboard apunta a ?status=VERIFYING. Se valida contra el enum para
  // no reenviar basura de la URL al API.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedStatus = searchParams.get('status');
  const initialStatus =
    requestedStatus && requestedStatus in PaymentStatus ? requestedStatus : 'ALL';

  const [statusFilter, setStatusFilter] = useState<string>(initialStatus);
  const [methodFilter, setMethodFilter] = useState<string>('ALL');

  // Verification Modal
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // Delete Manual Payment Modal
  const [paymentToDelete, setPaymentToDelete] = useState<Payment | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Manual Payment Modal
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [manualUnitId, setManualUnitId] = useState<string | null>(null);
  const [manualMemberId, setManualMemberId] = useState<string | null>(null);
  const [pendingCharges, setPendingCharges] = useState<Charge[]>([]);
  const [loadingCharges, setLoadingCharges] = useState(false);
  const [allocations, setAllocations] = useState<Record<string, number>>({});
  const [manualMethod, setManualMethod] = useState<PaymentMethod>(PaymentMethod.CASH);
  const [manualPaidAt, setManualPaidAt] = useState(toUtcDateString);
  const [manualReference, setManualReference] = useState('');
  const [manualNotes, setManualNotes] = useState('');
  const [manualProofUrl, setManualProofUrl] = useState('');
  const [creatingManual, setCreatingManual] = useState(false);
  const { upload: uploadProofFile, isUploading: isUploadingProof, reset: resetProofUpload } = useFileUpload('general');

  // Edit Manual Payment Modal
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [editUnitId, setEditUnitId] = useState<string | null>(null);
  const [editInitialUnitOption, setEditInitialUnitOption] = useState<SelectOption | null>(null);
  const [editMemberId, setEditMemberId] = useState<string | null>(null);
  const [editCharges, setEditCharges] = useState<Charge[]>([]);
  const [editLoadingCharges, setEditLoadingCharges] = useState(false);
  const [editAllocations, setEditAllocations] = useState<Record<string, number>>({});
  const [editMethod, setEditMethod] = useState<PaymentMethod>(PaymentMethod.CASH);
  const [editPaidAt, setEditPaidAt] = useState('');
  const [editReference, setEditReference] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editProofUrl, setEditProofUrl] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const unitChangedByUser = React.useRef(false);
  // Estado original del pago en edicion: se usa para detectar cuando el admin
  // vuelve a seleccionar la unidad del pago (no es un cambio deliberado) y para
  // restaurar miembro y asignaciones sin perder la edicion.
  const originalEditUnitIdRef = React.useRef<string | null>(null);
  const originalEditUnitOptionRef = React.useRef<SelectOption | null>(null);
  const originalEditMemberIdRef = React.useRef<string | null>(null);
  const originalEditAllocationsRef = React.useRef<Record<string, number>>({});

  // Proof Viewer Modal
  const [proofViewerUrl, setProofViewerUrl] = useState<string | null>(null);
  const [proofLoadError, setProofLoadError] = useState(false);


  const {
    data: payments,
    meta,
    loading,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    refresh,
    setFilters,
  } = usePaginatedList<Payment, { status?: PaymentStatus; method?: PaymentMethod }>({
    fetchFn: (params: QueryParams) => paymentRepo.getAllPayments({
      ...params,
      sortOrder: params.sortOrder?.toUpperCase() as 'ASC' | 'DESC' | undefined,
    }),
    initialFilters: initialStatus !== 'ALL' ? { status: initialStatus as PaymentStatus } : {},
    errorTitle: 'Error al cargar pagos',
  });

  // Update filters
  //
  // La URL es parte del estado: si el dropdown dice "Rechazados" pero el query param
  // sigue diciendo VERIFYING, recargar te devuelve a un filtro que ya no es el que ves,
  // y el enlace deja de ser compartible. Por eso cada cambio reescribe la URL.
  const handleStatusChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    updateFilters(newStatus, methodFilter);
    syncUrl(newStatus, methodFilter);
  };

  const handleMethodChange = (newMethod: string) => {
    setMethodFilter(newMethod);
    updateFilters(statusFilter, newMethod);
    syncUrl(statusFilter, newMethod);
  };

  /**
   * `setFilters` del hook hace merge (`{...filters, ...nuevos}`), no reemplazo: otras
   * pantallas dependen de eso para actualizar un filtro sin tocar los demas.
   *
   * Por eso hay que mandar SIEMPRE ambas claves. Omitir `status` al pasar a "Todos los
   * estados" no lo borraba: el merge conservaba el valor anterior y el filtro se quedaba
   * pegado. Con `undefined` explicito el merge lo sobreescribe, y `buildQueryString`
   * omite las claves undefined, asi que no viaja nada al backend.
   */
  const updateFilters = (status: string, method: string) => {
    setFilters({
      status: status !== 'ALL' ? (status as PaymentStatus) : undefined,
      method: method !== 'ALL' ? (method as PaymentMethod) : undefined,
    });
  };

  const syncUrl = (status: string, method: string) => {
    const params = new URLSearchParams();
    if (status !== 'ALL') params.set('status', status);
    if (method !== 'ALL') params.set('method', method);
    // replace: cambiar un filtro no deberia dejar una entrada por cada clic en el
    // historial del navegador.
    setSearchParams(params, { replace: true });
  };

  const clearFilters = () => {
    setStatusFilter('ALL');
    setMethodFilter('ALL');
    setSearch('');
    // Mismas claves explicitas que updateFilters: `setFilters({})` no borraba nada.
    updateFilters('ALL', 'ALL');
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  // El vacio de una lista filtrada no significa lo mismo que el de la lista completa.
  // Llegando desde la alerta "Pagos por verificar" del dashboard, un "No se encontraron
  // pagos" generico se lee como si algo hubiera fallado, cuando en realidad es la buena
  // noticia: ya no queda nada por revisar.
  const hasActiveFilter = statusFilter !== 'ALL' || methodFilter !== 'ALL' || !!search;
  const emptyMessage = !hasActiveFilter
    ? 'Todavia no hay pagos registrados.'
    : statusFilter === PaymentStatus.VERIFYING && methodFilter === 'ALL' && !search
      ? 'No hay pagos por verificar. Todo al dia.'
      : 'Ningun pago coincide con los filtros aplicados.';

  // El backend ya incluye memberName y unitNumber en cada pago. Antes se
  // cargaban TODOS los usuarios para resolver el nombre en el cliente, así que
  // los pagos de miembros fuera de esa primera página salían con el id en crudo.
  const getUserName = (payment: Payment) => {
    const name = payment.memberName?.trim();
    if (!name) return payment.memberId;
    return payment.unitNumber ? `${name} (${payment.unitNumber})` : name;
  };

  const getMethodLabel = (method: PaymentMethod | null) => {
    if (!method) return 'N/A';
    const labels: Record<PaymentMethod, string> = {
      [PaymentMethod.CASH]: 'Efectivo',
      [PaymentMethod.TRANSFER]: 'Transferencia',
      [PaymentMethod.CARD]: 'Tarjeta',
      [PaymentMethod.CHECK]: 'Cheque',
    };
    return labels[method] || method;
  };

  const getStatusConfig = (status: PaymentStatus) => {
    const configs: Record<PaymentStatus, { icon: React.ReactNode; className: string; label: string }> = {
      [PaymentStatus.PENDING]: {
        icon: <Clock size={12}/>,
        className: 'bg-slate-100 text-slate-600 border-slate-200',
        label: 'Pendiente',
      },
      [PaymentStatus.VERIFYING]: {
        icon: <AlertTriangle size={12}/>,
        className: 'bg-amber-50 text-amber-700 border-amber-200',
        label: 'En Verificacion',
      },
      [PaymentStatus.VERIFIED]: {
        icon: <CheckCircle size={12}/>,
        className: 'bg-green-50 text-green-700 border-green-200',
        label: 'Verificado',
      },
      [PaymentStatus.REJECTED]: {
        icon: <XCircle size={12}/>,
        className: 'bg-red-50 text-red-700 border-red-200',
        label: 'Rechazado',
      },
    };
    return configs[status];
  };

  const loadUnitOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const response = await unitRepo.getUnits({
      page: 1,
      limit: 20,
      search: inputValue || undefined,
      sortBy: 'name',
      sortOrder: 'asc',
    });
    return response.data.map(u => ({ value: u.id, label: formatUnitLabel(u) }));
  }, [unitRepo]);

  const resetManualModal = () => {
    setIsManualModalOpen(false);
    setManualUnitId(null);
    setManualMemberId(null);
    setPendingCharges([]);
    setAllocations({});
    setManualMethod(PaymentMethod.CASH);
    setManualPaidAt(toUtcDateString());
    setManualReference('');
    setManualNotes('');
    setManualProofUrl('');
    resetProofUpload();
  };

  const handleManualProofUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = await uploadProofFile(file);
    if (result) {
      setManualProofUrl(result.url);
    }
  };

  useEffect(() => {
    if (!manualUnitId) {
      setPendingCharges([]);
      setManualMemberId(null);
      return;
    }
    let cancelled = false;
    setLoadingCharges(true);
    chargeRepo
      .getAllCharges({ unitId: manualUnitId, limit: 100, sortBy: 'dueDate', sortOrder: 'ASC' })
      .then((response) => {
        if (cancelled) return;
        const owed = response.data.filter(
          (c) => c.status === ChargeStatus.PENDING || c.status === ChargeStatus.PARTIAL
        );
        setPendingCharges(owed);
        setManualMemberId(owed[0]?.memberId ?? null);
        setAllocations({});
      })
      .catch(() => {
        if (!cancelled) {
          toast.error('No se pudieron cargar los cargos pendientes de esta unidad');
          setPendingCharges([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCharges(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualUnitId]);

  const totalAllocated = useMemo(
    () => Object.values(allocations).reduce((sum, v) => sum + (v || 0), 0),
    [allocations]
  );

  const handleCreateManualPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualMemberId) {
      toast.warning('Selecciona una unidad con cargos pendientes');
      return;
    }
    const allocationEntries = Object.entries(allocations).filter(([, amount]) => amount > 0);
    if (allocationEntries.length === 0) {
      toast.warning('Asigna el pago a al menos un cargo');
      return;
    }

    setCreatingManual(true);
    try {
      const dto: CreateManualPaymentDTO = {
        memberId: manualMemberId,
        amount: totalAllocated,
        method: manualMethod,
        paidAt: manualPaidAt,
        transactionReference: manualReference || undefined,
        notes: manualNotes || undefined,
        proofUrl: manualProofUrl || undefined,
        allocations: allocationEntries.map(([chargeId, amount]) => ({ chargeId, amount })),
      };
      await withCreateNotification(() => paymentRepo.createManualPayment(dto));
      resetManualModal();
      refresh();
    } catch {
      // Error ya mostrado por el hook
    } finally {
      setCreatingManual(false);
    }
  };

  const currentAllocByCharge = useMemo(() => {
    const map: Record<string, number> = {};
    editingPayment?.allocations.forEach((a) => {
      map[a.chargeId] = a.amount;
    });
    return map;
  }, [editingPayment]);

  const editTotalAllocated = useMemo(
    () => Object.values(editAllocations).reduce((sum, v) => sum + (v || 0), 0),
    [editAllocations]
  );

  const editableEditCharges = useMemo(() => {
    if (!editingPayment || !editMemberId) return [];
    return editCharges.filter(
      (c) =>
        c.memberId === editMemberId &&
        (c.status === ChargeStatus.PENDING ||
          c.status === ChargeStatus.PARTIAL ||
          (currentAllocByCharge[c.id] || 0) > 0)
    );
  }, [editCharges, editMemberId, currentAllocByCharge, editingPayment]);

  const resetEditModal = () => {
    setEditingPayment(null);
    setEditUnitId(null);
    setEditInitialUnitOption(null);
    setEditMemberId(null);
    setEditCharges([]);
    setEditAllocations({});
    setSavingEdit(false);
  };

  const handleOpenEdit = (payment: Payment) => {
    const paymentAlloc: Record<string, number> = {};
    payment.allocations.forEach((a) => {
      paymentAlloc[a.chargeId] = a.amount;
    });
    unitChangedByUser.current = false;
    originalEditUnitIdRef.current = null;
    originalEditUnitOptionRef.current = null;
    originalEditMemberIdRef.current = payment.memberId;
    originalEditAllocationsRef.current = { ...paymentAlloc };
    setEditingPayment(payment);
    setEditInitialUnitOption(null);
    // La carga inicial usa getMemberCharges (keyed por el miembro del pago); la
    // unidad se preselecciona derivandola de esos cargos para que el selector
    // no arranque vacio (ver efecto de carga abajo). unitChangedByUser queda en
    // false en esta apertura, asi esa preseleccion no limpia las asignaciones.
    setEditUnitId(null);
    setEditMemberId(payment.memberId);
    setEditMethod(payment.method ?? PaymentMethod.CASH);
    setEditPaidAt(payment.paidAt ? payment.paidAt.slice(0, 10) : toUtcDateString());
    setEditReference(payment.transactionReference ?? '');
    setEditNotes(payment.notes ?? '');
    setEditProofUrl(payment.proofUrl ?? '');
    setEditAllocations(paymentAlloc);
  };

  const handleEditUnitChange = (newUnitId: string | null) => {
    // Volver a la unidad original del pago no es un cambio deliberado: se
    // restauran miembro y asignaciones originales para no perder la edicion.
    const isOriginalUnit =
      !!newUnitId && originalEditUnitIdRef.current != null && newUnitId === originalEditUnitIdRef.current;
    unitChangedByUser.current = !!newUnitId && !isOriginalUnit;
    if (isOriginalUnit) {
      setEditAllocations({ ...originalEditAllocationsRef.current });
      setEditMemberId(originalEditMemberIdRef.current);
      setEditInitialUnitOption(originalEditUnitOptionRef.current);
    } else {
      setEditInitialUnitOption(null);
    }
    setEditUnitId(newUnitId);
  };

  const buildAutoAllocations = (charges: Charge[], amount: number): Record<string, number> => {
    const result: Record<string, number> = {};
    let remaining = amount;
    for (const c of charges) {
      if (remaining <= 0) break;
      const max = Math.max(0, c.balance + (result[c.id] || 0));
      if (max <= 0) continue;
      const take = Math.min(remaining, max);
      result[c.id] = take;
      remaining -= take;
    }
    return result;
  };

  useEffect(() => {
    if (!editingPayment) return;
    let cancelled = false;
    setEditLoadingCharges(true);

    const loadPromise = editUnitId
      ? chargeRepo
          .getAllCharges({ unitId: editUnitId, limit: 100, sortBy: 'dueDate', sortOrder: 'ASC' })
          .then((r) => ({ charges: r.data, byUnit: true }))
      : chargeRepo
          .getMemberCharges(editingPayment.memberId, { limit: 100, sortBy: 'dueDate', sortOrder: 'ASC' })
          .then((r) => ({ charges: r.data, byUnit: false }));

    loadPromise
      .then(({ charges, byUnit }) => {
        if (cancelled) return;
        setEditCharges(charges);
        if (byUnit) {
          if (unitChangedByUser.current) {
            // El admin cambio de unidad a proposito: re-derivar al miembro con
            // cargos pendientes, limpiar las asignaciones previas y auto-asignar
            // el monto del pago a los cargos de ese miembro para que el boton
            // de Guardar quede habilitado de inmediato.
            const owed = charges.filter(
              (c) => c.status === ChargeStatus.PENDING || c.status === ChargeStatus.PARTIAL
            );
            const member = owed[0]?.memberId ?? null;
            setEditMemberId(member);
            const memberCharges = member ? owed.filter((c) => c.memberId === member) : [];
            setEditAllocations(buildAutoAllocations(memberCharges, editingPayment.amount));
          } else {
            // Preseleccion automatica de la unidad (misma unidad): mantener el
            // miembro del pago y las asignaciones originales.
            setEditMemberId(editingPayment.memberId);
            setEditAllocations({ ...originalEditAllocationsRef.current });
          }
        } else {
          setEditMemberId(editingPayment.memberId);
          // Preseleccionar la unidad del pago (derivada de sus cargos) para que
          // el selector no arranque vacio. Re-dispara el efecto por la ruta de
          // unidad, pero con unitChangedByUser en false no se tocan las
          // asignaciones ni el miembro.
          const firstCharge = charges[0];
          setEditUnitId((prev) => prev ?? firstCharge?.unitId ?? null);
          // Guardar la unidad/miembro/asignaciones originales para restaurarlos
          // si el admin vuelve a seleccionar la unidad del pago.
          if (firstCharge?.unitId) {
            originalEditUnitIdRef.current = firstCharge.unitId;
            if (firstCharge.unitName) {
              originalEditUnitOptionRef.current = { value: firstCharge.unitId, label: firstCharge.unitName };
            }
          }
          // El valor de la unidad viene de la API de cargos, pero el select es
          // asincrono y aun no tiene la opcion en su cache: asi puede mostrar
          // la etiqueta de la unidad preseleccionada.
          if (originalEditUnitOptionRef.current) {
            setEditInitialUnitOption(originalEditUnitOptionRef.current);
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast.error('No se pudieron cargar los cargos');
          setEditCharges([]);
        }
      })
      .finally(() => {
        if (!cancelled) setEditLoadingCharges(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editUnitId, editingPayment]);

  const handleEditProofUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = await uploadProofFile(file);
    if (result) {
      setEditProofUrl(result.url);
    }
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPayment) return;
    if (!editMemberId) {
      toast.warning('Selecciona una unidad con cargos');
      return;
    }
    const allocationEntries = Object.entries(editAllocations).filter(([, amount]) => amount > 0);
    if (allocationEntries.length === 0) {
      toast.warning('Asigna el pago a al menos un cargo');
      return;
    }

    setSavingEdit(true);
    try {
      const dto: UpdateManualPaymentDTO = {
        memberId: editMemberId,
        amount: editTotalAllocated,
        method: editMethod,
        paidAt: editPaidAt,
        transactionReference: editReference || null,
        notes: editNotes || null,
        proofUrl: editProofUrl || null,
        allocations: allocationEntries.map(([chargeId, amount]) => ({ chargeId, amount })),
      };
      await withNotification(
        () => paymentRepo.updateManualPayment(editingPayment.id, dto),
        'Pago actualizado correctamente'
      );
      resetEditModal();
      refresh();
    } catch {
      // Error ya mostrado por el hook
    } finally {
      setSavingEdit(false);
    }
  };

  const handleViewProof = (url: string | null) => {
    if (url) {
      setProofLoadError(false);
      setProofViewerUrl(url);
    } else {
      toast.warning('Este pago no tiene comprobante adjunto');
    }
  };

  const handleVerifyPayment = async (payment: Payment, approved: boolean) => {
    if (!canUpdatePayment) return;
    if (!approved && !rejectionReason.trim()) {
      toast.warning('Debes especificar un motivo de rechazo');
      return;
    }

    setProcessing(true);
    try {
      const data: VerifyPaymentDTO = {
        approved,
        ...(approved ? {} : { rejectionReason: rejectionReason.trim() }),
      };

      await withNotification(
        () => paymentRepo.verifyPayment(payment.id, data),
        approved ? 'Pago verificado correctamente' : 'Pago rechazado'
      );

      setSelectedPayment(null);
      setIsRejectModalOpen(false);
      setRejectionReason('');
      refresh();
    } catch {
      // Error handled by hook
    } finally {
      setProcessing(false);
    }
  };

  const handleDeletePayment = async (payment: Payment) => {
    if (!canUpdatePayment) return;
    setDeleting(true);
    try {
      await withNotification(
        () => paymentRepo.deleteManualPayment(payment.id),
        'Pago eliminado correctamente'
      );
      setPaymentToDelete(null);
      setIsDeleteModalOpen(false);
      refresh();
    } catch {
      // Error ya mostrado por el hook
    } finally {
      setDeleting(false);
    }
  };

  const columns: ColumnDef<Payment>[] = useMemo(() => [
    {
      id: 'member',
      header: 'Residente',
      cell: (payment) => (
        <div className="flex items-center gap-2">
          <User size={14} className="text-slate-400"/>
          <span className="font-medium text-slate-800">
            {payment.memberName || getUserName(payment)}
          </span>
        </div>
      ),
    },
    {
      id: 'unit',
      header: 'Unidad',
      cell: (payment) => (
        <span className="text-sm text-slate-600">{payment.unitNumber || '-'}</span>
      ),
    },
    {
      id: 'amount',
      header: 'Monto',
      className: 'text-right',
      cell: (payment) => (
        <div className="text-right">
          <span className="font-mono font-bold text-slate-800">${payment.amount.toLocaleString()}</span>
          {payment.allocatedAmount > 0 && (
            <p className="text-[10px] text-green-600">
              Aplicado: ${payment.allocatedAmount.toLocaleString()}
            </p>
          )}
          {payment.unallocatedAmount > 0 && (
            <p className="text-[10px] text-amber-600">
              Sin aplicar: ${payment.unallocatedAmount.toLocaleString()}
            </p>
          )}
        </div>
      ),
    },
    {
      id: 'method',
      header: 'Metodo',
      cell: (payment) => (
        <span className="text-sm text-slate-600">{getMethodLabel(payment.method)}</span>
      ),
    },
    {
      id: 'paidAt',
      header: 'Fecha Pago',
      cell: (payment) => (
        <span className="text-sm text-slate-500">
          {payment.paidAt ? formatDate(payment.paidAt) : '-'}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Estado',
      cell: (payment) => {
        const config = getStatusConfig(payment.status);
        return (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${config.className}`}>
            {config.icon}
            {config.label}
          </span>
        );
      },
    },
    {
      id: 'proof',
      header: 'Comprobante',
      cell: (payment) => (
        payment.proofUrl ? (
          <button
            onClick={() => handleViewProof(payment.proofUrl)}
            className="text-primary-600 hover:text-primary-700 flex items-center gap-1 text-xs font-medium"
          >
            <Image size={14}/>
            Ver
          </button>
        ) : (
          <span className="text-slate-400 text-xs">Sin comprobante</span>
        )
      ),
    },
  ], [formatDate]);

  const filters = (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        <Filter size={16} className="text-slate-400" />
        <select
          className="rounded-lg border border-slate-300 text-sm bg-white px-3 py-2"
          value={statusFilter}
          onChange={e => handleStatusChange(e.target.value)}
        >
          <option value="ALL">Todos los estados</option>
          <option value={PaymentStatus.VERIFYING}>En Verificacion</option>
          <option value={PaymentStatus.VERIFIED}>Verificados</option>
          <option value={PaymentStatus.REJECTED}>Rechazados</option>
          <option value={PaymentStatus.PENDING}>Pendientes</option>
        </select>
      </div>
      <select
        className="rounded-lg border border-slate-300 text-sm bg-white px-3 py-2"
        value={methodFilter}
        onChange={e => handleMethodChange(e.target.value)}
      >
        <option value="ALL">Todos los metodos</option>
        <option value={PaymentMethod.TRANSFER}>Transferencia</option>
        <option value={PaymentMethod.CASH}>Efectivo</option>
        <option value={PaymentMethod.CARD}>Tarjeta</option>
        <option value={PaymentMethod.CHECK}>Cheque</option>
      </select>
      {hasActiveFilter && (
        <button
          type="button"
          onClick={clearFilters}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 px-2 py-2 whitespace-nowrap"
        >
          <X size={14} /> Limpiar filtros
        </button>
      )}
    </div>
  );

  return (
    <>
      <DataTable
        data={payments}
        columns={columns}
        keyExtractor={(payment) => payment.id}
        meta={meta}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        title="Verificacion de Pagos"
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por residente o referencia..."
        loading={loading}
        emptyMessage={emptyMessage}
        filters={filters}
        actions={
          <Button onClick={() => setIsManualModalOpen(true)} icon={<Plus size={16}/>}>
            Registrar Pago
          </Button>
        }
        rowActions={(payment) => (
          <div className="flex items-center gap-2">
            {payment.status === PaymentStatus.VERIFYING && canUpdatePayment && (
              <>
                <button
                  onClick={() => handleVerifyPayment(payment, true)}
                  className="text-xs font-bold text-green-600 hover:underline flex items-center gap-1"
                  disabled={processing}
                >
                  <CheckCircle size={12}/>
                  Verificar
                </button>
                <button
                  onClick={() => {
                    setSelectedPayment(payment);
                    setIsRejectModalOpen(true);
                  }}
                  className="text-xs font-bold text-red-600 hover:underline flex items-center gap-1"
                  disabled={processing}
                >
                  <XCircle size={12}/>
                  Rechazar
                </button>
              </>
            )}
            {payment.status === PaymentStatus.VERIFIED && canUpdatePayment && (
              <button
                onClick={() => handleOpenEdit(payment)}
                className="text-xs font-bold text-slate-600 hover:underline flex items-center gap-1"
              >
                <Pencil size={12}/>
                Editar
              </button>
            )}
            {payment.status !== PaymentStatus.VERIFIED && canUpdatePayment && (
              <button
                onClick={() => handleOpenEdit(payment)}
                className="text-xs font-bold text-slate-600 hover:underline flex items-center gap-1"
              >
                <Pencil size={12}/>
                Corregir
              </button>
            )}
            {canUpdatePayment && (
              <button
                onClick={() => {
                  setPaymentToDelete(payment);
                  setIsDeleteModalOpen(true);
                }}
                className="text-xs font-bold text-red-600 hover:underline flex items-center gap-1"
                disabled={processing || deleting}
              >
                <XCircle size={12}/>
                Eliminar
              </button>
            )}
            <button
              onClick={() => setSelectedPayment(payment)}
              className="text-xs font-bold text-slate-600 hover:underline flex items-center gap-1"
            >
              <Eye size={12}/>
              Detalle
            </button>
          </div>
        )}
      />

      {/* Payment Detail Modal */}
      {selectedPayment && !isRejectModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg">Detalle de Pago</h3>
              <button onClick={() => setSelectedPayment(null)} className="text-slate-400 hover:text-slate-600">
                <XCircle size={20}/>
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-400 uppercase font-bold">Residente</p>
                  <p className="font-medium text-slate-800">
                    {selectedPayment.memberName || getUserName(selectedPayment)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400 uppercase font-bold">Monto</p>
                  <p className="text-2xl font-bold text-primary-600">${selectedPayment.amount.toLocaleString()}</p>
                </div>
              </div>

              {/* Status Badge */}
              <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg">
                <span className="text-sm text-slate-600">Estado</span>
                {(() => {
                  const config = getStatusConfig(selectedPayment.status);
                  return (
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${config.className}`}>
                      {config.icon}
                      {config.label}
                    </span>
                  );
                })()}
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-400 mb-1">Metodo</p>
                  <p className="font-medium">{getMethodLabel(selectedPayment.method)}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-400 mb-1">Fecha de Pago</p>
                  <p className="font-medium">
                    {selectedPayment.paidAt ? formatDate(selectedPayment.paidAt) : '-'}
                  </p>
                </div>
                {selectedPayment.transactionReference && (
                  <div className="bg-slate-50 p-3 rounded-lg col-span-2">
                    <p className="text-xs text-slate-400 mb-1">Referencia</p>
                    <p className="font-mono font-medium">{selectedPayment.transactionReference}</p>
                  </div>
                )}
              </div>

              {/* Allocations */}
              {selectedPayment.allocations.length > 0 && (
                <div>
                  <h4 className="text-xs text-slate-400 uppercase font-bold mb-2 flex items-center gap-1">
                    <DollarSign size={12}/>
                    Aplicado a Cargos
                  </h4>
                  <div className="space-y-2">
                    {selectedPayment.allocations.map(alloc => (
                      <div key={alloc.id} className="flex justify-between items-center bg-green-50 p-3 rounded-lg border border-green-100">
                        <div>
                          <p className="font-medium text-slate-700">{alloc.chargeConcept || 'Cargo'}</p>
                          {alloc.chargePeriod && (
                            <p className="text-xs text-slate-500">Periodo: {alloc.chargePeriod}</p>
                          )}
                        </div>
                        <span className="font-mono font-bold text-green-700">${alloc.amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Unallocated Warning */}
              {selectedPayment.unallocatedAmount > 0 && (
                <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg flex items-start gap-2">
                  <AlertTriangle size={16} className="text-amber-600 mt-0.5"/>
                  <div>
                    <p className="font-bold text-amber-800 text-sm">Monto sin aplicar</p>
                    <p className="text-xs text-amber-700">
                      ${selectedPayment.unallocatedAmount.toLocaleString()} pendiente de asignar a cargos
                    </p>
                  </div>
                </div>
              )}

              {/* Rejection Reason */}
              {selectedPayment.status === PaymentStatus.REJECTED && selectedPayment.rejectionReason && (
                <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
                  <p className="text-xs text-red-400 uppercase font-bold mb-1">Motivo de Rechazo</p>
                  <p className="text-sm text-red-700">{selectedPayment.rejectionReason}</p>
                </div>
              )}

              {/* Proof Button */}
              {selectedPayment.proofUrl && (
                <Button
                  variant="secondary"
                  onClick={() => handleViewProof(selectedPayment.proofUrl)}
                  icon={<Image size={16}/>}
                  className="w-full"
                >
                  Ver Comprobante
                </Button>
              )}

              {/* Invoice Links */}
              {(selectedPayment.pdfUrl || selectedPayment.xmlUrl) && (
                <div className="flex gap-2">
                  {selectedPayment.pdfUrl && (
                    <Button
                      variant="secondary"
                      onClick={() => window.open(selectedPayment.pdfUrl!, '_blank', 'noopener,noreferrer')}
                      icon={<FileText size={16}/>}
                      className="flex-1"
                    >
                      PDF Factura
                    </Button>
                  )}
                  {selectedPayment.xmlUrl && (
                    <Button
                      variant="secondary"
                      onClick={() => window.open(selectedPayment.xmlUrl!, '_blank', 'noopener,noreferrer')}
                      icon={<FileText size={16}/>}
                      className="flex-1"
                    >
                      XML
                    </Button>
                  )}
                </div>
              )}

              {/* Notes */}
              {selectedPayment.notes && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-400 mb-1">Notas</p>
                  <p className="text-sm text-slate-600">{selectedPayment.notes}</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="p-5 border-t border-slate-100 flex gap-3">
              {selectedPayment.status === PaymentStatus.VERIFYING && canUpdatePayment && (
                <>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setIsRejectModalOpen(true);
                    }}
                    className="flex-1"
                    icon={<XCircle size={16}/>}
                  >
                    Rechazar
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => handleVerifyPayment(selectedPayment, true)}
                    className="flex-1"
                    isLoading={processing}
                    icon={<CheckCircle size={16}/>}
                  >
                    Verificar Pago
                  </Button>
                </>
              )}
              {(selectedPayment.status !== PaymentStatus.VERIFYING || !canUpdatePayment) && (
                <Button
                  variant="secondary"
                  onClick={() => setSelectedPayment(null)}
                  className="w-full"
                >
                  Cerrar
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {isRejectModalOpen && selectedPayment && canUpdatePayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2">
                <XCircle size={20} className="text-red-500"/>
                Rechazar Pago
              </h3>
              <button onClick={() => {
                setIsRejectModalOpen(false);
                setRejectionReason('');
              }} className="text-slate-400 hover:text-slate-600">
                <XCircle size={20}/>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-red-50 p-3 rounded-lg text-sm text-red-700 border border-red-100">
                Estas a punto de rechazar un pago de <strong>${selectedPayment.amount.toLocaleString()}</strong> de{' '}
                <strong>{selectedPayment.memberName || getUserName(selectedPayment)}</strong>.
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Motivo del Rechazo <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-red-100 focus:border-red-300"
                  placeholder="Describe el motivo por el cual se rechaza este pago..."
                  value={rejectionReason}
                  onChange={e => setRejectionReason(e.target.value)}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsRejectModalOpen(false);
                    setRejectionReason('');
                  }}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  onClick={() => handleVerifyPayment(selectedPayment, false)}
                  className="flex-1 !bg-red-600 hover:!bg-red-700"
                  isLoading={processing}
                  disabled={!rejectionReason.trim()}
                  icon={<XCircle size={16}/>}
                >
                  Confirmar Rechazo
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Manual Payment Modal */}
      {isDeleteModalOpen && paymentToDelete && canUpdatePayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2">
                <XCircle size={20} className="text-red-500"/>
                Eliminar Pago
              </h3>
              <button onClick={() => {
                setIsDeleteModalOpen(false);
                setPaymentToDelete(null);
              }} className="text-slate-400 hover:text-slate-600">
                <XCircle size={20}/>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-red-50 p-3 rounded-lg text-sm text-red-700 border border-red-100">
                Estas a punto de eliminar un pago por <strong>${paymentToDelete.amount.toLocaleString()}</strong> de{' '}
                <strong>{paymentToDelete.memberName || getUserName(paymentToDelete)}</strong>.
                Se revertira el saldo de los cargos a los que estaba aplicado. Esta accion no se puede deshacer.
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setPaymentToDelete(null);
                  }}
                  className="flex-1"
                  disabled={deleting}
                >
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  onClick={() => handleDeletePayment(paymentToDelete)}
                  className="flex-1 !bg-red-600 hover:!bg-red-700"
                  isLoading={deleting}
                  icon={<XCircle size={16}/>}
                >
                  Confirmar Eliminacion
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Proof Viewer Modal */}
      {proofViewerUrl && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={(e) => e.target === e.currentTarget && setProofViewerUrl(null)}
        >
          <div
            className="relative w-full max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200 bg-white shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                  {proofViewerUrl?.toLowerCase().endsWith('.pdf') ? (
                    <FileText size={20} className="text-primary-600" />
                  ) : (
                    <Image size={20} className="text-primary-600" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">Comprobante de Pago</h3>
                  <p className="text-xs text-slate-500">
                    {proofViewerUrl?.toLowerCase().endsWith('.pdf') ? 'Documento PDF' : 'Vista previa del comprobante'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <a
                  href={proofViewerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 transition-colors"
                >
                  <Eye size={16} />
                  Abrir en nueva pestaña
                </a>
                <button
                  onClick={() => setProofViewerUrl(null)}
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  <XCircle size={20} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto bg-slate-100 flex items-center justify-center p-6">
              {proofLoadError ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle size={32} className="text-red-500" />
                  </div>
                  <p className="text-slate-600 font-medium">No se pudo cargar el comprobante</p>
                  <p className="text-slate-500 text-sm mt-1">
                    Intenta abrirlo en una nueva pestaña usando el botón de arriba
                  </p>
                </div>
              ) : proofViewerUrl?.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={proofViewerUrl}
                  className="w-full h-[70vh] rounded-lg bg-white"
                  title="Comprobante de pago PDF"
                  onError={() => setProofLoadError(true)}
                />
              ) : (
                <img
                  src={proofViewerUrl ?? ''}
                  alt="Comprobante de pago"
                  className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
                  onError={() => setProofLoadError(true)}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Manual Payment Modal */}
      {isManualModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg">Registrar Pago Manual</h3>
              <button onClick={resetManualModal} className="text-slate-400 hover:text-slate-600">
                <XCircle size={20}/>
              </button>
            </div>

            <form onSubmit={handleCreateManualPayment} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700">
                Usa esto para registrar un pago recibido fuera del sistema (efectivo, transferencia sin
                comprobante, etc.). Queda verificado de inmediato y se aplica a los cargos que elijas.
              </div>

              <AsyncSearchableSelect
                label="Unidad"
                loadOptions={loadUnitOptions}
                value={manualUnitId}
                onChange={setManualUnitId}
                placeholder="Buscar unidad..."
                isClearable
                defaultOptions
                minLength={0}
              />

              {manualUnitId && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Aplicar a cargos pendientes
                  </label>
                  {loadingCharges ? (
                    <p className="text-sm text-slate-400">Cargando cargos...</p>
                  ) : pendingCharges.length === 0 ? (
                    <p className="text-sm text-slate-400">Esta unidad no tiene cargos pendientes.</p>
                  ) : (
                    <div className="space-y-2 border border-slate-200 rounded-lg p-3">
                      {pendingCharges.map((charge) => (
                        <div key={charge.id} className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">{charge.concept}</p>
                            <p className="text-xs text-slate-400">
                              Saldo: ${charge.balance.toLocaleString()}
                              {charge.period ? ` · ${charge.period}` : ''}
                            </p>
                          </div>
                          <input
                            type="number"
                            min={0}
                            max={charge.balance}
                            step="0.01"
                            className="w-28 rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-right"
                            placeholder="0.00"
                            value={allocations[charge.id] || ''}
                            onChange={(e) => {
                              const value = Math.min(parseFloat(e.target.value) || 0, charge.balance);
                              setAllocations({ ...allocations, [charge.id]: value });
                            }}
                          />
                        </div>
                      ))}
                      <div className="flex justify-between pt-2 border-t border-slate-100 text-sm font-bold text-slate-700">
                        <span>Total a registrar</span>
                        <span>${totalAllocated.toLocaleString()}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Método</label>
                  <select
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm"
                    value={manualMethod}
                    onChange={(e) => setManualMethod(e.target.value as PaymentMethod)}
                  >
                    <option value={PaymentMethod.CASH}>Efectivo</option>
                    <option value={PaymentMethod.TRANSFER}>Transferencia</option>
                    <option value={PaymentMethod.CARD}>Tarjeta</option>
                    <option value={PaymentMethod.CHECK}>Cheque</option>
                  </select>
                </div>
                <Input
                  label="Fecha del pago"
                  type="date"
                  value={manualPaidAt}
                  onChange={(e) => setManualPaidAt(e.target.value)}
                  required
                />
              </div>

              <Input
                label="Referencia (opcional)"
                value={manualReference}
                onChange={(e) => setManualReference(e.target.value)}
                placeholder="Folio, número de cheque, etc."
              />

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Comprobante / imagen (opcional)
                </label>
                <div className="flex items-center gap-3">
                  <label
                    className={`cursor-pointer inline-flex items-center justify-center px-4 py-2 border border-slate-300 shadow-sm text-sm font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 transition-colors w-full ${isUploadingProof ? 'opacity-50 cursor-wait' : ''}`}
                  >
                    {isUploadingProof ? (
                      <Loader2 size={16} className="mr-2 text-slate-500 animate-spin" />
                    ) : (
                      <Upload size={16} className="mr-2 text-slate-500" />
                    )}
                    {isUploadingProof ? 'Subiendo...' : manualProofUrl ? 'Archivo adjunto (cambiar)' : 'Subir archivo'}
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      className="hidden"
                      disabled={isUploadingProof}
                      onChange={handleManualProofUpload}
                    />
                  </label>
                  {manualProofUrl && (
                    <a
                      href={manualProofUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-primary-600 hover:underline flex items-center gap-1 flex-shrink-0"
                    >
                      <Eye size={12}/> Ver
                    </a>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Notas (opcional)</label>
                <textarea
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm min-h-[70px]"
                  value={manualNotes}
                  onChange={(e) => setManualNotes(e.target.value)}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="secondary" className="flex-1" onClick={resetManualModal}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  className="flex-1"
                  icon={<Plus size={16}/>}
                  isLoading={creatingManual}
                  disabled={!manualMemberId || totalAllocated <= 0}
                >
                  Registrar Pago
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Manual Payment Modal */}
      {editingPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg">Editar Pago Manual</h3>
              <button onClick={resetEditModal} className="text-slate-400 hover:text-slate-600">
                <XCircle size={20}/>
              </button>
            </div>

            <form onSubmit={handleSaveEdit} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700">
                Corrige los datos del pago. Al guardar se revierten los saldos de los cargos
                originales y se aplican los nuevos.
              </div>

              <AsyncSearchableSelect
                label="Unidad"
                loadOptions={loadUnitOptions}
                value={editUnitId}
                onChange={handleEditUnitChange}
                placeholder="Buscar unidad..."
                isClearable
                defaultOptions
                minLength={0}
                initialOption={editInitialUnitOption}
              />

              {editMemberId && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Aplicar a cargos
                    <span className="text-slate-400 font-normal">
                      {' '}· {editingPayment.memberName || editingPayment.memberId}
                    </span>
                  </label>
                  {editLoadingCharges ? (
                    <p className="text-sm text-slate-400">Cargando cargos...</p>
                  ) : editableEditCharges.length === 0 ? (
                    <p className="text-sm text-slate-400">No hay cargos disponibles.</p>
                  ) : (
                    <div className="space-y-2 border border-slate-200 rounded-lg p-3">
                      {editableEditCharges.map((charge) => {
                        const applied = currentAllocByCharge[charge.id] || 0;
                        const max = Math.max(0, charge.balance + applied);
                        return (
                          <div key={charge.id} className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-700 truncate">{charge.concept}</p>
                              <p className="text-xs text-slate-400">
                                Saldo: ${charge.balance.toLocaleString()}
                                {applied > 0 && (
                                  <span className="text-green-600"> · Aplicado: ${applied.toLocaleString()}</span>
                                )}
                                {charge.period ? ` · ${charge.period}` : ''}
                              </p>
                            </div>
                            <input
                              type="number"
                              min={0}
                              max={max}
                              step="0.01"
                              className="w-28 rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-right"
                              placeholder="0.00"
                              value={editAllocations[charge.id] || ''}
                              disabled={max <= 0}
                              onChange={(e) => {
                                const value = Math.min(parseFloat(e.target.value) || 0, max);
                                setEditAllocations({ ...editAllocations, [charge.id]: value });
                              }}
                            />
                          </div>
                        );
                      })}
                      <div className="flex justify-between pt-2 border-t border-slate-100 text-sm font-bold text-slate-700">
                        <span>Total del pago</span>
                        <span>${editTotalAllocated.toLocaleString()}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Método</label>
                  <select
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm"
                    value={editMethod}
                    onChange={(e) => setEditMethod(e.target.value as PaymentMethod)}
                  >
                    <option value={PaymentMethod.CASH}>Efectivo</option>
                    <option value={PaymentMethod.TRANSFER}>Transferencia</option>
                    <option value={PaymentMethod.CARD}>Tarjeta</option>
                    <option value={PaymentMethod.CHECK}>Cheque</option>
                  </select>
                </div>
                <Input
                  label="Fecha del pago"
                  type="date"
                  value={editPaidAt}
                  onChange={(e) => setEditPaidAt(e.target.value)}
                  required
                />
              </div>

              <Input
                label="Referencia (opcional)"
                value={editReference}
                onChange={(e) => setEditReference(e.target.value)}
                placeholder="Folio, número de cheque, etc."
              />

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Comprobante / imagen (opcional)
                </label>
                <div className="flex items-center gap-3">
                  <label
                    className={`cursor-pointer inline-flex items-center justify-center px-4 py-2 border border-slate-300 shadow-sm text-sm font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 transition-colors w-full ${isUploadingProof ? 'opacity-50 cursor-wait' : ''}`}
                  >
                    {isUploadingProof ? (
                      <Loader2 size={16} className="mr-2 text-slate-500 animate-spin" />
                    ) : (
                      <Upload size={16} className="mr-2 text-slate-500" />
                    )}
                    {isUploadingProof ? 'Subiendo...' : editProofUrl ? 'Archivo adjunto (cambiar)' : 'Subir archivo'}
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      className="hidden"
                      disabled={isUploadingProof}
                      onChange={handleEditProofUpload}
                    />
                  </label>
                  {editProofUrl && (
                    <a
                      href={editProofUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-primary-600 hover:underline flex items-center gap-1 flex-shrink-0"
                    >
                      <Eye size={12}/> Ver
                    </a>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Notas (opcional)</label>
                <textarea
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm min-h-[70px]"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="secondary" className="flex-1" onClick={resetEditModal}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  className="flex-1"
                  icon={<Pencil size={16}/>}
                  isLoading={savingEdit}
                  disabled={!editMemberId || editableEditCharges.length === 0 || editTotalAllocated <= 0}
                >
                  Guardar Cambios
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
